import { useEffect, useRef, useState } from 'react';

import { timeoutFetchSentryGuard } from 'Fetch';
import { getEnquiryData } from 'PrivateApis';

import { VehicleDetails } from 'Utilities/profiling/@types';

export type EnquiryKeeperDetails = {
  isLoading: boolean;
  keeperName: string | null;
  keeperType: VehicleDetails['keeperType'] | null;
};

const EMPTY: EnquiryKeeperDetails = { isLoading: false, keeperName: null, keeperType: null };

export const useGetEnquiryKeeperDetails = (
  dpEnquiryId?: number,
  vrm?: string,
): EnquiryKeeperDetails => {
  const [details, setDetails] = useState<EnquiryKeeperDetails>({
    ...EMPTY,
    isLoading: Boolean(dpEnquiryId),
  });

  const resolvedIdRef = useRef<number | undefined>(undefined);

  const vrmRef = useRef(vrm);
  vrmRef.current = vrm;

  useEffect(() => {
    if (!dpEnquiryId) {
      resolvedIdRef.current = undefined;
      setDetails(EMPTY);
      return undefined;
    }

    setDetails({ isLoading: true, keeperName: null, keeperType: null });

    const abortController = new AbortController();

    getEnquiryData(dpEnquiryId, {}, 'vehicleDetails', abortController)
      .then((enquiry) => {
        if (abortController.signal.aborted) {
          return;
        }
        resolvedIdRef.current = dpEnquiryId;
        setDetails({
          isLoading: false,
          keeperName: enquiry?.vehicleDetails?.keeperName ?? null,
          keeperType: enquiry?.vehicleDetails?.keeperType ?? null,
        });
      })
      .catch((err) => {
        if (abortController.signal.aborted || err?.name === 'AbortError') {
          return;
        }
        resolvedIdRef.current = dpEnquiryId;
        setDetails(EMPTY);
        const exception = err instanceof Error ? err : new Error(String(err));
        timeoutFetchSentryGuard(err, () => globalThis?.Sentry?.captureException?.(exception, {
          extra: { dpEnquiryId, vrm: vrmRef.current },
          tags: { fetch: 'useGetEnquiryKeeperDetails' },
        }));
      });

    return () => abortController.abort();
  }, [dpEnquiryId]);

  if (dpEnquiryId && dpEnquiryId !== resolvedIdRef.current && !details.isLoading) {
    return { isLoading: true, keeperName: null, keeperType: null };
  }

  return details;
};
